import {
  devModeEnabled,
  getCurrentToken,
  getDevUserSub,
  loginRedirect,
  trySilentRenew,
} from "@/lib/auth";
import type {
  ActivityItem,
  BackupsOverview,
  BoardStatusSegments,
  Board,
  BoardCreatePayload,
  BoardDetail,
  BoardShare,
  BoardSubmitPayload,
  BoardSubmitResult,
  BoardUpdatePayload,
  Cell,
  CellWritePayload,
  ColumnCreatePayload,
  ColumnDef,
  ColumnUpdatePayload,
  Comment,
  CommentCreatePayload,
  FileRef,
  MailContent,
  BoardPerson,
  HomeCalendarItem,
  MeetingSeries,
  MeetingSeriesCreatePayload,
  Memo,
  MemoAuditEntry,
  MemoEntry,
  MemoEntryKind,
  MemoHeaderPayload,
  MemoSummary,
  MondayBoardPlan,
  RequestForm,
  MondayBoardSummary,
  MondayImportReport,
  MondayImportRequestPayload,
  MySubmissionItem,
  OpenWorkItem,
  NotificationItem,
  NotificationDefaults,
  NotificationPrefs,
  Row,
  LinkCandidate,
  MemoOrigin,
  RowActionPayload,
  RowActionResult,
  RowLinks,
  RowShares,
  SubscriptionState,
  RowCreatePayload,
  RowUpdatePayload,
  SearchResponse,
  SectionHistoryEntry,
  SeriesTask,
  SharedTicketItem,
  UserMe,
  UserSearchResult,
  UUID,
  Workspace,
  WorkspaceCreatePayload,
  WorkspaceMember,
  WorkspaceMemberAddPayload,
  WorkspaceMemberRoleUpdatePayload,
  WorkspaceUpdatePayload,
} from "@/lib/types";

const baseUrl = (): string => import.meta.env.VITE_API_URL.replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;
  constructor(status: number, message: string, detail: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  // When true, a 401 will not trigger silent renew (used for the renew path itself).
  noAuthRetry?: boolean;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (devModeEnabled()) {
    return { "X-Dev-User": getDevUserSub() };
  }
  const token = await getCurrentToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${baseUrl()}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(await authHeaders()),
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "GET",
    headers,
    body,
    credentials: "omit",
  });

  if (res.status === 401 && !opts.noAuthRetry && !devModeEnabled()) {
    const renewed = await trySilentRenew();
    if (renewed?.accessToken) {
      return request<T>(path, { ...opts, noAuthRetry: true });
    }
    await loginRedirect();
    throw new ApiError(401, "unauthorized", null);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const data: unknown = text ? safeJson(text) : null;
  if (!res.ok) {
    const message =
      (typeof data === "object" && data && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : res.statusText) || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  // Auth ------------------------------------------------------------
  me: () => request<UserMe>("/auth/me"),

  // Notification preferences ---------------------------------------
  getNotificationPrefs: () => request<NotificationPrefs>("/me/notification-prefs"),
  updateNotificationPrefs: (body: {
    matrix: NotificationPrefs["matrix"];
    always_notify_directed: boolean;
  }) => request<NotificationPrefs>("/me/notification-prefs", { method: "PUT", body }),

  // Admin org-wide notification defaults (flow-admin only).
  getNotificationDefaults: () => request<NotificationDefaults>("/notification-defaults"),
  updateNotificationDefaults: (body: {
    matrix: NotificationDefaults["matrix"];
    locked: NotificationDefaults["locked"];
  }) => request<NotificationDefaults>("/notification-defaults", { method: "PUT", body }),

  // Admin backup status (flow-admin only). Read-only metadata; there is
  // deliberately no download or trigger endpoint.
  getBackups: () => request<BackupsOverview>("/admin/backups"),

  // Workspaces ------------------------------------------------------
  listWorkspaces: () => request<Workspace[]>("/workspaces"),
  createWorkspace: (payload: WorkspaceCreatePayload) =>
    request<Workspace>("/workspaces", { method: "POST", body: payload }),
  updateWorkspace: (workspaceId: UUID, payload: WorkspaceUpdatePayload) =>
    request<Workspace>(`/workspaces/${workspaceId}`, { method: "PATCH", body: payload }),
  deleteWorkspace: (workspaceId: UUID) =>
    request<void>(`/workspaces/${workspaceId}`, { method: "DELETE" }),

  // Boards ----------------------------------------------------------
  listMyBoards: (limit?: number) => request<Board[]>("/boards", { query: { limit } }),
  listBoards: (workspaceId: UUID) => request<Board[]>(`/workspaces/${workspaceId}/boards`),
  createBoard: (workspaceId: UUID, payload: BoardCreatePayload) =>
    request<Board>(`/workspaces/${workspaceId}/boards`, { method: "POST", body: payload }),
  getBoard: (boardId: UUID, opts: { cursor?: number; limit?: number } = {}) =>
    request<BoardDetail>(`/boards/${boardId}`, {
      query: { cursor: opts.cursor, limit: opts.limit },
    }),
  updateBoard: (boardId: UUID, payload: BoardUpdatePayload) =>
    request<Board>(`/boards/${boardId}`, { method: "PATCH", body: payload }),
  deleteBoard: (boardId: UUID) => request<void>(`/boards/${boardId}`, { method: "DELETE" }),
  archiveBoard: (boardId: UUID) => request<void>(`/boards/${boardId}/archive`, { method: "POST" }),
  unarchiveBoard: (boardId: UUID) =>
    request<void>(`/boards/${boardId}/unarchive`, { method: "POST" }),
  reorderBoards: (orders: { board_id: UUID; position: number }[]) =>
    request<void>(`/boards/reorder`, { method: "POST", body: { orders } }),
  duplicateBoard: (boardId: UUID, name?: string) =>
    request<Board>(`/boards/${boardId}/duplicate`, { method: "POST", body: { name } }),

  // Cross-workspace sharing -----------------------------------------
  listBoardShares: (boardId: UUID) => request<BoardShare[]>(`/boards/${boardId}/shares`),
  shareBoard: (boardId: UUID, workspaceId: UUID) =>
    request<void>(`/boards/${boardId}/shares`, {
      method: "POST",
      body: { workspace_id: workspaceId },
    }),
  unshareBoard: (boardId: UUID, workspaceId: UUID) =>
    request<void>(`/boards/${boardId}/shares/${workspaceId}`, { method: "DELETE" }),

  // Org-wide workspace directory (per-ticket share dialog's department picker).
  listAllWorkspaces: () => request<Workspace[]>("/workspaces/all"),

  // Per-ticket (per-row) sharing ------------------------------------
  listRowShares: (rowId: UUID) => request<RowShares>(`/rows/${rowId}/shares`),
  shareRow: (rowId: UUID, body: { user_ids?: UUID[]; workspace_ids?: UUID[] }) =>
    request<RowShares>(`/rows/${rowId}/shares`, { method: "POST", body }),
  unshareRow: (rowId: UUID, body: { user_id?: UUID; workspace_id?: UUID }) =>
    request<void>(`/rows/${rowId}/shares`, { method: "DELETE", body }),
  getSharedTickets: () => request<SharedTicketItem[]>("/me/shared-tickets"),

  // Verknüpfte Tickets (links + Folgetickets) -----------------------
  getRowLinks: (rowId: UUID) => request<RowLinks>(`/rows/${rowId}/links`),
  getLinkCandidates: (rowId: UUID, q: string) =>
    request<LinkCandidate[]>(`/rows/${rowId}/link-candidates`, { query: { q } }),
  createTicketLink: (rowId: UUID, body: { target_row_id: UUID; link_type: string }) =>
    request<RowLinks>(`/rows/${rowId}/links`, { method: "POST", body }),
  createFollowUp: (rowId: UUID, body: { cells: Record<string, unknown> }) =>
    request<{ row_id: UUID; links: RowLinks }>(`/rows/${rowId}/follow-ups`, {
      method: "POST",
      body,
    }),
  deleteTicketLink: (linkId: UUID) => request<void>(`/links/${linkId}`, { method: "DELETE" }),

  // Protokoll origin of a to-do (breadcrumb + deep-link). Batched so Home can
  // reuse it; the detail panel calls it with a single row id.
  memoOrigins: (rowIds: UUID[]) =>
    request<{ origins: Record<string, MemoOrigin> }>("/rows/memo-origins", {
      method: "POST",
      body: { row_ids: rowIds },
    }),

  // Submit (public-form path) ---------------------------------------
  getSubmitInfo: (boardId: UUID) => request<BoardDetail>(`/boards/${boardId}/submit-info`),
  submitToBoard: (boardId: UUID, payload: BoardSubmitPayload) =>
    request<BoardSubmitResult>(`/boards/${boardId}/submit`, { method: "POST", body: payload }),
  // Manual ticket creation (member-gated) — a department lead files a ticket on
  // behalf of a requester who cannot write a message themselves. Same payload as
  // submit, but requires board-write membership and a tickets-kind board.
  createTicket: (boardId: UUID, payload: BoardSubmitPayload) =>
    request<BoardSubmitResult>(`/boards/${boardId}/tickets`, { method: "POST", body: payload }),

  // Columns ---------------------------------------------------------
  createColumn: (boardId: UUID, payload: ColumnCreatePayload) =>
    request<ColumnDef>(`/boards/${boardId}/columns`, { method: "POST", body: payload }),
  updateColumn: (columnId: UUID, payload: ColumnUpdatePayload) =>
    request<ColumnDef>(`/columns/${columnId}`, { method: "PATCH", body: payload }),
  addColumnOption: (column: ColumnDef, label: string, color?: string): Promise<ColumnDef> => {
    const cfg = (column.config ?? {}) as {
      options?: Array<{ id: string; label: string; color?: string }>;
    };
    const existing = Array.isArray(cfg.options) ? cfg.options : [];
    const id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `opt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const nextOption = { id, label, color: color ?? "gray" };
    const nextConfig = { ...cfg, options: [...existing, nextOption] };
    return request<ColumnDef>(`/columns/${column.id}`, {
      method: "PATCH",
      body: { config: nextConfig },
    });
  },
  deleteColumn: (columnId: UUID) => request<void>(`/columns/${columnId}`, { method: "DELETE" }),

  // Rows ------------------------------------------------------------
  createRow: (boardId: UUID, payload: RowCreatePayload = {}) =>
    request<Row>(`/boards/${boardId}/rows`, { method: "POST", body: payload }),
  updateRow: (rowId: UUID, payload: RowUpdatePayload) =>
    request<Row>(`/rows/${rowId}`, { method: "PATCH", body: payload }),
  deleteRow: (rowId: UUID) => request<void>(`/rows/${rowId}`, { method: "DELETE" }),
  restoreRow: (rowId: UUID) =>
    request<{ restored: UUID[] }>(`/rows/${rowId}/restore`, { method: "POST" }),
  duplicateRow: (
    rowId: UUID,
    opts: {
      withComments?: boolean;
      /** Re-home the copy under this top-level row (sub-item copy under a parent). */
      targetParentRowId?: UUID | null;
      /** Set the copy's cell in this column so a top-level copy lands in another group. */
      groupColumnId?: UUID | null;
      groupValue?: unknown;
    } = {},
  ) =>
    request<Row>(`/rows/${rowId}/duplicate`, {
      method: "POST",
      body: {
        with_comments: opts.withComments ?? false,
        target_parent_row_id: opts.targetParentRowId ?? null,
        group_column_id: opts.groupColumnId ?? null,
        group_value: opts.groupValue ?? null,
      },
    }),
  duplicateGroup: (
    boardId: UUID,
    body: {
      group_column_id: UUID;
      source_value: unknown;
      new_label: string;
      with_comments?: boolean;
    },
  ) =>
    request<{ group_value: unknown; label: string; row_count: number }>(
      `/boards/${boardId}/groups/duplicate`,
      { method: "POST", body },
    ),

  // Cells -----------------------------------------------------------
  upsertCell: (rowId: UUID, columnDefId: UUID, payload: CellWritePayload) =>
    request<Cell>(`/rows/${rowId}/cells/${columnDefId}`, { method: "PUT", body: payload }),

  // Search ----------------------------------------------------------
  search: (q: string, limit?: number) =>
    request<SearchResponse>("/search", { query: { q, limit } }),

  // Mein Postfach ---------------------------------------------------
  listMySubmissions: (limit?: number) =>
    request<MySubmissionItem[]>("/me/submissions", { query: { limit } }),
  listOpenWork: () => request<OpenWorkItem[]>("/me/open-work"),
  listHomeCalendar: (start: string, end: string) =>
    request<HomeCalendarItem[]>("/me/calendar", { query: { start, end } }),
  listRequestForms: () => request<RequestForm[]>("/me/request-forms"),
  listIdeaForms: () => request<RequestForm[]>("/me/idea-forms"),
  listBoardPeople: (boardId: UUID) => request<BoardPerson[]>(`/boards/${boardId}/people`),
  listRowMentionPeople: (rowId: UUID) => request<BoardPerson[]>(`/rows/${rowId}/mention-people`),

  // Notifications ---------------------------------------------------
  listNotifications: (limit?: number, unreadOnly?: boolean) =>
    request<NotificationItem[]>("/me/notifications", {
      query: { limit, unread_only: unreadOnly ? "true" : undefined },
    }),
  getNotificationsUnreadCount: () => request<{ count: number }>("/me/notifications/unread-count"),
  markNotificationRead: (id: UUID) =>
    request<{ marked: number }>(`/me/notifications/${id}/read`, { method: "POST" }),
  markAllNotificationsRead: () =>
    request<{ marked: number }>("/me/notifications/read-all", { method: "POST" }),
  markRowNotificationsRead: (rowId: UUID) =>
    request<{ marked: number }>("/me/notifications/read-by-row", {
      method: "POST",
      body: { row_id: rowId },
    }),

  // Pins ------------------------------------------------------------
  listPinnedBoards: () => request<Board[]>("/me/pinned-boards"),
  pinBoard: (boardId: UUID) => request<void>(`/boards/${boardId}/pin`, { method: "POST" }),
  unpinBoard: (boardId: UUID) => request<void>(`/boards/${boardId}/pin`, { method: "DELETE" }),

  // Comments --------------------------------------------------------
  listRowComments: (rowId: UUID) => request<Comment[]>(`/rows/${rowId}/comments`),
  listRowActivity: (rowId: UUID) => request<ActivityItem[]>(`/rows/${rowId}/activity`),
  getBoardStatusSegments: (boardId: UUID) =>
    request<BoardStatusSegments>(`/boards/${boardId}/status-segments`),
  createRowComment: (rowId: UUID, payload: CommentCreatePayload) =>
    request<Comment>(`/rows/${rowId}/comments`, { method: "POST", body: payload }),
  deleteComment: (commentId: UUID) => request<void>(`/comments/${commentId}`, { method: "DELETE" }),
  updateComment: (commentId: UUID, body: string) =>
    request<Comment>(`/comments/${commentId}`, { method: "PATCH", body: { body } }),
  setCommentLike: (commentId: UUID, liked: boolean) =>
    request<{ like_count: number; liked_by_me: boolean }>(`/comments/${commentId}/like`, {
      method: liked ? "POST" : "DELETE",
    }),

  // Row subscriptions ----------------------------------------------
  getRowSubscription: (rowId: UUID) => request<SubscriptionState>(`/rows/${rowId}/subscription`),
  subscribeRow: (rowId: UUID) =>
    request<SubscriptionState>(`/rows/${rowId}/subscribe`, { method: "POST" }),
  unsubscribeRow: (rowId: UUID) =>
    request<SubscriptionState>(`/rows/${rowId}/subscribe`, { method: "DELETE" }),

  // Workspace members ----------------------------------------------
  listWorkspaceMembers: (workspaceId: UUID) =>
    request<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`),
  addWorkspaceMember: (workspaceId: UUID, payload: WorkspaceMemberAddPayload) =>
    request<WorkspaceMember>(`/workspaces/${workspaceId}/members`, {
      method: "POST",
      body: payload,
    }),
  updateWorkspaceMemberRole: (
    workspaceId: UUID,
    userId: UUID,
    payload: WorkspaceMemberRoleUpdatePayload,
  ) =>
    request<WorkspaceMember>(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "PATCH",
      body: payload,
    }),
  removeWorkspaceMember: (workspaceId: UUID, userId: UUID) =>
    request<void>(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "DELETE",
    }),

  // Users (global JIT pool search) ---------------------------------
  searchUsers: (q: string, limit?: number) =>
    request<UserSearchResult[]>("/users/search", { query: { q, limit } }),

  // Org-wide Kürzel directory for the memo highlighter.
  userDirectory: () =>
    request<{ id: string; username: string; display_name: string | null }[]>("/users/directory"),

  // Dashboard stats ------------------------------------------------
  getAssignedCount: () => request<{ count: number }>("/me/assigned-count"),

  // Row actions ----------------------------------------------------
  performRowAction: (rowId: UUID, payload: RowActionPayload) =>
    request<RowActionResult>(`/rows/${rowId}/actions`, {
      method: "POST",
      body: payload,
    }),

  // File uploads ---------------------------------------------------
  // Multipart, so it can't go through request() (which forces JSON). Sets no
  // Content-Type — the browser adds the multipart boundary itself.
  uploadFile: async (file: File): Promise<FileRef> => {
    const form = new FormData();
    form.append("file", file);
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(await authHeaders()),
    };
    const res = await fetch(buildUrl("/uploads"), {
      method: "POST",
      headers,
      body: form,
      credentials: "omit",
    });
    const text = await res.text();
    const data: unknown = text ? safeJson(text) : null;
    if (!res.ok) {
      const message =
        (typeof data === "object" && data && "detail" in data
          ? String((data as { detail: unknown }).detail)
          : res.statusText) || `HTTP ${res.status}`;
      throw new ApiError(res.status, message, data);
    }
    return data as FileRef;
  },

  // Parse a stored .eml/.msg for the in-app viewer. `key` is the storage key
  // (the part after /api/files/), which contains a slash — kept unencoded as a
  // path (storage sanitizes filenames to a URL-safe charset).
  getMailContent: (key: string) => request<MailContent>(`/mail/${key}`),

  // Monday.com migration -------------------------------------------
  // Token is sent over HTTPS, used in-memory by the backend for the run, and
  // never persisted.
  mondayPreviewBoards: (token: string) =>
    request<MondayBoardSummary[]>("/imports/monday/preview", {
      method: "POST",
      body: { token },
    }),
  mondayBoardPlan: (token: string, mondayBoardId: string) =>
    request<MondayBoardPlan>("/imports/monday/columns", {
      method: "POST",
      body: { token, monday_board_id: mondayBoardId },
    }),
  mondayImportBoard: (payload: MondayImportRequestPayload) =>
    request<MondayImportReport>("/imports/monday/board", {
      method: "POST",
      body: payload,
    }),

  // Meeting memos ---------------------------------------------------
  listMeetingSeries: () => request<MeetingSeries[]>("/meeting-series"),
  createMeetingSeries: (payload: MeetingSeriesCreatePayload) =>
    request<MeetingSeries>("/meeting-series", { method: "POST", body: payload }),
  getSeries: (seriesId: UUID) => request<MeetingSeries>(`/meeting-series/${seriesId}`),
  updateSeries: (seriesId: UUID, payload: Partial<MeetingSeriesCreatePayload>) =>
    request<MeetingSeries>(`/meeting-series/${seriesId}`, { method: "PATCH", body: payload }),
  deleteSeries: (seriesId: UUID) =>
    request<void>(`/meeting-series/${seriesId}`, { method: "DELETE" }),
  listSeriesMemos: (seriesId: UUID) => request<MemoSummary[]>(`/meeting-series/${seriesId}/memos`),
  createMemo: (seriesId: UUID, payload: MemoHeaderPayload) =>
    request<Memo>(`/meeting-series/${seriesId}/memos`, { method: "POST", body: payload }),
  copyForwardMemo: (seriesId: UUID, payload: MemoHeaderPayload & { source_memo_id: UUID }) =>
    request<Memo>(`/meeting-series/${seriesId}/copy-forward`, { method: "POST", body: payload }),
  getMemo: (memoId: UUID) => request<Memo>(`/memos/${memoId}`),
  deleteMemo: (memoId: UUID) => request<void>(`/memos/${memoId}`, { method: "DELETE" }),
  patchMemo: (memoId: UUID, payload: Partial<MemoHeaderPayload> & { title?: string }) =>
    request<Memo>(`/memos/${memoId}`, { method: "PATCH", body: payload }),
  finalizeMemo: (memoId: UUID) => request<Memo>(`/memos/${memoId}/finalize`, { method: "POST" }),
  reopenMemo: (memoId: UUID) => request<Memo>(`/memos/${memoId}/reopen`, { method: "POST" }),
  requestMemoPrep: (memoId: UUID) =>
    request<{ notified: number }>(`/memos/${memoId}/request-prep`, { method: "POST" }),
  getMemoAudit: (memoId: UUID) => request<MemoAuditEntry[]>(`/memos/${memoId}/audit`),
  getMemoSectionHistory: (memoId: UUID, anchorId: UUID) =>
    request<SectionHistoryEntry[]>(`/memos/${memoId}/sections/${anchorId}/history`),
  addMemoBlock: (memoId: UUID, title: string) =>
    request<Memo>(`/memos/${memoId}/blocks`, { method: "POST", body: { title } }),
  patchMemoBlock: (blockId: UUID, payload: { title?: string }) =>
    request<Memo>(`/memo-blocks/${blockId}`, { method: "PATCH", body: payload }),
  // Reorder is an INTENT the server applies to the stored order — never a position the
  // browser computed from what it could see (that is how two sections shared one number).
  moveMemoBlock: (blockId: UUID, direction: "up" | "down") =>
    request<Memo>(`/memo-blocks/${blockId}/move`, { method: "POST", body: { direction } }),
  deleteMemoBlock: (blockId: UUID) =>
    request<Memo>(`/memo-blocks/${blockId}`, { method: "DELETE" }),
  addMemoEntry: (
    memoId: UUID,
    payload: {
      block_id: UUID;
      body?: string;
      after_entry_id?: UUID;
      kind?: MemoEntryKind;
      /** Restores a deleted line's own running-log date on undo. Omitted ⇒ the memo's
       *  meeting_date, which is what a genuinely NEW entry wants. */
      log_date?: string | null;
    },
  ) => request<MemoEntry>(`/memos/${memoId}/entries`, { method: "POST", body: payload }),
  patchMemoEntry: (entryId: UUID, payload: { body?: string; position?: number; block_id?: UUID }) =>
    request<MemoEntry>(`/memo-entries/${entryId}`, { method: "PATCH", body: payload }),
  deleteMemoEntry: (entryId: UUID) =>
    request<Memo>(`/memo-entries/${entryId}`, { method: "DELETE" }),
  /** `subheadingId`: omitted → the Abschnitt's END (a Zwischenüberschrift with its points —
   *  the legacy shape, sent without the field); `null` → the Abschnitt's top level; an id →
   *  the end of that Zwischenabschnitt. The server tells omitted from null apart — and
   *  JSON.stringify drops an `undefined` key, so `undefined` really is omitted on the wire. */
  moveMemoEntry: (entryId: UUID, blockId: UUID, subheadingId?: UUID | null) =>
    request<Memo>(`/memo-entries/${entryId}/move`, {
      method: "POST",
      body: { block_id: blockId, target_subheading_id: subheadingId },
    }),
  setMemoEntryLogDate: (entryId: UUID, logDate: string | null) =>
    request<MemoEntry>(`/memo-entries/${entryId}/log-date`, {
      method: "POST",
      body: { log_date: logDate },
    }),
  setEntryDue: (entryId: UUID, due_date: string | null) =>
    request<MemoEntry>(`/memo-entries/${entryId}/due`, { method: "POST", body: { due_date } }),
  completeEntryTask: (entryId: UUID, solution: string | null) =>
    request<MemoEntry>(`/memo-entries/${entryId}/complete`, { method: "POST", body: { solution } }),
  reopenEntryTask: (entryId: UUID) =>
    request<MemoEntry>(`/memo-entries/${entryId}/reopen`, { method: "POST" }),
  retireEntry: (entryId: UUID, retired: boolean) =>
    request<MemoEntry>(`/memo-entries/${entryId}/retire`, { method: "POST", body: { retired } }),
  setMemoParticipants: (memoId: UUID, participant_ids: UUID[]) =>
    request<Memo>(`/memos/${memoId}/participants`, { method: "PATCH", body: { participant_ids } }),
  listSeriesTasks: (seriesId: UUID) => request<SeriesTask[]>(`/meeting-series/${seriesId}/tasks`),
};
